export interface RuntimeEnv {
  /** API origin. Empty string means same-origin, which the Vite dev proxy handles. */
  GATEWAY: string;
  /** React Router basename. */
  BASENAME: string;
  CLERK_PUBLISHABLE_KEY: string;
  MAP_STYLE_LIGHT: string;
  MAP_STYLE_DARK: string;
  SSE_PATH: string;
  ENVIRONMENT: 'local' | 'dev' | 'staging' | 'prod';
}

declare global {
  interface Window {
    env?: Partial<RuntimeEnv>;
  }
}

/** Keys without a sensible default. Missing any of them is a deployment error. */
const REQUIRED = [
  'CLERK_PUBLISHABLE_KEY',
  'MAP_STYLE_LIGHT',
  'MAP_STYLE_DARK',
  'SSE_PATH',
] as const satisfies readonly (keyof RuntimeEnv)[];

export class RuntimeConfigError extends Error {
  // Declared explicitly rather than as a constructor parameter property:
  // `erasableSyntaxOnly` forbids syntax that needs a runtime transform.
  readonly missing: string[];

  constructor(missing: string[]) {
    super(`window.env is missing: ${missing.join(', ')} - check /env.js`);
    this.name = 'RuntimeConfigError';
    this.missing = missing;
  }
}

let cached: RuntimeEnv | null = null;

/**
 * The validated config, resolved on first use.
 *
 * Deliberately lazy rather than a module-level `export const env = readEnv()`. Imports
 * are evaluated before any statement in the importing module, so an eager call would
 * throw during `main.tsx`'s import graph -- before its try/catch could render the
 * "Configuration error" card. The white screen this is meant to prevent would be caused
 * by the prevention itself.
 */
export function getEnv(): RuntimeEnv {
  cached ??= readEnv();
  return cached;
}

/**
 * Seeds (or clears) the singleton.
 *
 * `main.tsx` calls this with the value it already validated, so the config error card
 * stays the single place a bad `env.js` is reported. Tests call it with no argument to
 * reset between cases.
 */
export function primeEnv(next?: RuntimeEnv) {
  cached = next ?? null;
}

export function readEnv(source: Partial<RuntimeEnv> | undefined = window.env): RuntimeEnv {
  const raw = { BASENAME: '/', GATEWAY: '', ...(source ?? {}) } as RuntimeEnv;
  const missing = REQUIRED.filter((key) => !raw[key]);
  if (missing.length > 0) {
    throw new RuntimeConfigError(missing);
  }
  return Object.freeze(raw);
}
